import { useState, useCallback } from 'react'

const useFirmSelection = () => {
  const [selectedFirmIds, setSelectedFirmIds] = useState({})
  const [selectedFirmMeta, setSelectedFirmMeta] = useState({})
  const [selectAllMode, setSelectAllMode] = useState(null)
  const [excludedFirms, setExcludedFirms] = useState({})

  const deriveSelectionForPage = useCallback((mappedFirms, rawFirmList, isInitialLoad) => {
    if (selectAllMode === 'all') {
      const allIds = {}
      mappedFirms.forEach(f => { allIds[f.id] = true })
      return allIds
    }

    if (selectAllMode === 'allExcept') {
      const ids = {}
      mappedFirms.forEach(f => { ids[f.id] = !excludedFirms[f.id] })
      return ids
    }

    if (isInitialLoad) {
      const pageIds = rawFirmList.reduce((acc, firm) => {
        const id = firm.id || firm.identifier
        if (firm.selected) acc[id] = true
        return acc
      }, {})
      return { ...pageIds, ...selectedFirmIds }
    }

    return null
  }, [selectAllMode, excludedFirms, selectedFirmIds])

  const updateMeta = useCallback((firms) => {
    setSelectedFirmMeta(prev => {
      const next = { ...prev }
      firms.forEach(f => {
        next[f.id] = { firmType: f.firmType, identifier: f.identifier || f.id }
      })
      return next
    })
  }, [])

  const toggleFirm = useCallback((firmId, desiredState, firms) => {
    const isCurrentlySelected = selectedFirmIds[firmId]
    const newValue = desiredState !== undefined
      ? desiredState
      : !isCurrentlySelected

    setSelectedFirmIds(prev => ({ ...prev, [firmId]: newValue }))
    setSelectAllMode(prev => (prev === 'all' ? 'allExcept' : prev))

    if (newValue) {
      const firm = firms.find(f => f.id === firmId)
      if (firm) {
        setSelectedFirmMeta(prev => ({
          ...prev,
          [firmId]: { firmType: firm.firmType, identifier: firm.identifier || firm.id }
        }))
      }
    }

    if (selectAllMode === 'all' || selectAllMode === 'allExcept') {
      if (!newValue) {
        const firm = firms.find(f => f.id === firmId)
        if (firm) {
          setExcludedFirms(prev => ({ ...prev, [firmId]: { firmType: firm.firmType, identifier: firm.identifier || firm.id } }))
        }
      } else {
        setExcludedFirms(prev => {
          const next = { ...prev }
          delete next[firmId]
          return next
        })
      }
    }
  }, [selectedFirmIds, selectAllMode])

  const selectAll = useCallback((firms) => {
    const allIds = {}
    firms.forEach(f => { allIds[f.id] = true })

    setSelectAllMode('all')
    setExcludedFirms({})
    setSelectedFirmIds(allIds)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectAllMode(null)
    setSelectedFirmIds({})
    setSelectedFirmMeta({})
    setExcludedFirms({})
  }, [])

  const applySelection = useCallback((ids) => {
    setSelectedFirmIds(ids)
  }, [])

  const buildPayload = useCallback((campaignId, firms, totalCount) => {
    if (selectAllMode === 'all') {
      return { campaignId, items: [], mode: 'all' }
    }

    if (selectAllMode === 'allExcept') {
      const excludedItems = Object.values(excludedFirms)
      if (excludedItems.length === 0) {
        return { campaignId, items: [], mode: 'all' }
      }
      return { campaignId, items: excludedItems, mode: 'exclude' }
    }

    const allSelectedIds = Object.keys(selectedFirmIds).filter(id => selectedFirmIds[id])

    if (allSelectedIds.length === 0) {
      return { campaignId, items: [], mode: 'all' }
    }

    const items = allSelectedIds.map(id => {
      if (selectedFirmMeta[id]) return selectedFirmMeta[id]
      const firm = firms.find(f => f.id === id)
      if (firm) return { firmType: firm.firmType, identifier: firm.identifier || firm.id }
      return { identifier: id }
    })

    return { campaignId, items, mode: 'include' }
  }, [selectAllMode, excludedFirms, selectedFirmIds, selectedFirmMeta])

  return {
    selectedFirmIds,
    selectAllMode,
    excludedCount: Object.keys(excludedFirms).length,

    deriveSelectionForPage,
    applySelection,
    updateMeta,
    toggleFirm,
    selectAll,
    clearSelection,
    buildPayload
  }
}

export default useFirmSelection
